/**
 * tracking.js — Unified remarketing & analytics event module.
 *
 * Wraps:
 *   1. Backend event sink  → POST /api/public/tracking/event  (MongoDB, attribution)
 *   2. Facebook Pixel      → window.fbq  (via useAnalytics injection)
 *   3. Google Analytics 4  → window.gtag (via useAnalytics injection)
 *   4. TikTok Pixel        → window.ttq  (injected here on demand)
 *
 * Usage:
 *   import { initTracking, track } from "@/utils/tracking";
 *
 *   // Call once when restaurant data loads:
 *   initTracking({ restaurantId, sessionId, integrations, apiBase });
 *
 *   // Fire events anywhere:
 *   track("view_product", { product_id: "abc", cart_value: 12.5 });
 */

import axios from "axios";

// ─── Session ID (stable per browser tab) ─────────────────────────────────────

function getSessionId() {
  if (typeof window === "undefined") return "ssr";
  let sid = sessionStorage.getItem("_kaiso_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem("_kaiso_sid", sid);
  }
  return sid;
}

// ─── State ────────────────────────────────────────────────────────────────────

let _config = null;         // set by initTracking()
let _tiktokReady = false;
const _injected = { tiktok: false };

// ─── Init ─────────────────────────────────────────────────────────────────────

/**
 * Call once when restaurant data is available.
 * @param {Object} opts
 * @param {string} opts.restaurantId
 * @param {string} [opts.sessionId]     defaults to stable session-storage UUID
 * @param {Object} [opts.integrations]  restaurant.integrations object
 * @param {string} [opts.apiBase]       e.g. "https://api.example.com/api"
 */
export function initTracking({ restaurantId, sessionId, integrations = {}, apiBase = "" }) {
  // Auto-capture Meta cookies for CAPI match quality
  const fbp = _getCookie("_fbp");
  const fbc = _getCookie("_fbc");

  _config = {
    restaurantId,
    sessionId: sessionId || getSessionId(),
    integrations,
    apiBase,
    fbp,
    fbc,
  };

  _injectTikTok(integrations?.tiktok_pixel_id);
  _configGoogleAds(integrations?.google_ads_id);
}

// ─── Google Ads ───────────────────────────────────────────────────────────────

function _configGoogleAds(adsId) {
  if (!adsId) return;
  // gtag is injected async by useAnalytics — retry up to 5s after initTracking
  const attempt = (remaining) => {
    if (typeof window?.gtag === "function") {
      window.gtag("config", adsId);
      return;
    }
    if (remaining > 0) setTimeout(() => attempt(remaining - 1), 1000);
  };
  attempt(5);
}

// ─── TikTok Pixel injection ───────────────────────────────────────────────────

function _injectTikTok(pixelId) {
  if (!pixelId || _injected.tiktok || typeof document === "undefined") return;
  _injected.tiktok = true;

  const script = document.createElement("script");
  script.innerHTML = `
    !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i=ttq._i||{},ttq._i[e]=[],ttq._eid=ttq._eid||{},ttq._eid[e]=e;
    var o=document.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;
    var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
    ttq.load("${pixelId}");ttq.page();}(window,document,"ttq");
  `;
  document.head.appendChild(script);

  _tiktokReady = true;
}

// ─── Meta cookie capture ──────────────────────────────────────────────────────

function _getCookie(name) {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

// ─── FB Pixel helper ──────────────────────────────────────────────────────────

// Map internal event names to Facebook Conversion API event names
// CAPI-forwarded events: page_view, view_product, add_to_cart, start_checkout, purchase, lead_captured, abandoned_cart
const FB_EVENT_MAP = {
  view_menu:              null,              // no standard FB event
  view_product:           "ViewContent",     // DPA retargeting, product interest
  add_to_cart:            "AddToCart",       // cart abandonment retargeting
  start_checkout:         "InitiateCheckout", // checkout abandonment funnel
  generate_payment_link:  null,              // not a standard FB event
  purchase:               "Purchase",        // conversion tracking, ROI
  lead_captured:          "Lead",            // lead scoring, lead gen campaigns
  abandoned_cart:         "InitiateCheckout", // maps to same as start_checkout (Meta dedup handles this)
  table_qr_open:          null,              // not a standard FB event
  page_view:              "PageView",        // top-of-funnel awareness/reach
};

const GA4_EVENT_MAP = {
  view_product:           "view_item",
  add_to_cart:            "add_to_cart",
  start_checkout:         "begin_checkout",
  purchase:               "purchase",
  lead_captured:          "generate_lead",
  page_view:              "page_view",
};

const TTQ_EVENT_MAP = {
  view_product:           "ViewContent",
  add_to_cart:            "AddToCart",
  start_checkout:         "InitiateCheckout",
  purchase:               "PlaceAnOrder",
  lead_captured:          "SubmitForm",
};

// ─── Main track() ─────────────────────────────────────────────────────────────

/**
 * Fire a tracking event across all configured platforms.
 *
 * @param {string} event   One of ALLOWED_EVENTS
 * @param {Object} [data]
 * @param {string}  [data.product_id]
 * @param {number}  [data.cart_value]
 * @param {string}  [data.source]         order source label
 * @param {string}  [data.phone]          raw phone — will be hashed before sending
 * @param {string}  [data.fbp]            _fbp cookie (improves Meta CAPI match quality)
 * @param {string}  [data.fbc]            _fbc cookie
 * @param {Object}  [data.metadata]       arbitrary extra fields (capped server-side)
 */
// Events forwarded to Meta CAPI — need a shared event_id for deduplication.
const CAPI_EVENTS = new Set(["page_view", "view_product", "add_to_cart", "start_checkout", "abandoned_cart", "purchase", "lead_captured"]);

export async function track(event, data = {}) {
  if (!_config) return;  // initTracking() not called yet

  const { restaurantId, sessionId, integrations, apiBase, fbp: configFbp, fbc: configFbc } = _config;
  const { product_id, cart_value, source, phone, email, fbp, fbc, metadata = {} } = data;

  // Generate a stable event_id for browser↔CAPI deduplication on CAPI-forwarded events.
  // PageView ALWAYS needs event_id for dedup (required by CAPI).
  const eventId = CAPI_EVENTS.has(event)
    ? (metadata.event_id || crypto.randomUUID())
    : undefined;
  const metadataWithId = eventId ? { ...metadata, event_id: eventId } : metadata;

  // Pixel sets cookies asynchronously; use their current values at each event.
  const fbpValue = fbp || _getCookie("_fbp") || configFbp;
  const fbcValue = fbc || _getCookie("_fbc") || configFbc;

  // 1. Backend sink (fire-and-forget)
  // NOTE: Phone is sent raw to backend; backend hashes it for consistency (SHA-256 with E.164 normalization)
  axios.post(`${apiBase}/api/public/tracking/event`, {
    restaurant_id: restaurantId,
    event,
    session_id: sessionId,
    source: source || null,
    product_id: product_id || null,
    cart_value: typeof cart_value === "number" ? cart_value : null,
    phone_hash: phone || null,
    email: email || null,
    fbp: fbpValue || null,
    fbc: fbcValue || null,
    metadata: metadataWithId,
    page_url: typeof window !== "undefined" ? window.location.href : null,
  }).catch(() => {});  // non-critical

  // 2. Facebook Pixel — pass eventID for server-side dedup
  const fbEvent = FB_EVENT_MAP[event];
  if (fbEvent && typeof window?.fbq === "function") {
    const fbParams = {};
    if (cart_value != null) { fbParams.value = cart_value; fbParams.currency = metadata.currency || integrations?.currency || "EUR"; }
    if (product_id)          fbParams.content_ids = [product_id];
    if (product_id)          fbParams.content_type = "product";
    if (data.product_name)   fbParams.content_name = data.product_name;
    const fbOptions = eventId ? { eventID: eventId } : {};
    window.fbq("track", fbEvent, fbParams, fbOptions);
  }

  // 3. Google Analytics 4
  const ga4Event = GA4_EVENT_MAP[event];
  if (ga4Event && typeof window?.gtag === "function") {
    const ga4Params = {};
    if (cart_value != null)  ga4Params.value = cart_value;
    if (product_id)          ga4Params.item_id = product_id;
    if (data.product_name)   ga4Params.item_name = data.product_name;
    if (integrations?.currency) ga4Params.currency = integrations.currency;
    window.gtag("event", ga4Event, ga4Params);
  }

  // 3b. Google Ads conversion (purchase only)
  if (event === "purchase" && integrations?.google_ads_id && typeof window?.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: integrations.google_ads_id,
      value: cart_value || 0,
      currency: integrations?.currency || "EUR",
    });
  }

  // 4. TikTok Pixel
  const ttqEvent = TTQ_EVENT_MAP[event];
  if (ttqEvent && _tiktokReady && typeof window?.ttq === "object") {
    const ttqParams = {};
    if (cart_value != null)  ttqParams.value = cart_value;
    if (product_id)          ttqParams.content_id = product_id;
    if (data.product_name)   ttqParams.content_name = data.product_name;
    if (integrations?.currency) ttqParams.currency = integrations.currency;
    window.ttq.track(ttqEvent, ttqParams);
  }
}

// ─── Convenience helpers ──────────────────────────────────────────────────────
// Note: CAPI-forwarded events (page_view, view_product, add_to_cart, start_checkout, purchase, lead_captured, abandoned_cart)
// always need event_id for deduplication. Ensure metadata.event_id is set before calling.

export const trackPageView    = (data = {})    => track("page_view",             { ...data, metadata: { ...data.metadata, event_id: (data.metadata?.event_id || crypto.randomUUID()) } });
export const trackViewMenu    = (data = {})    => track("view_menu",             data);
export const trackViewProduct = (data = {})    => track("view_product",          { ...data, metadata: { ...data.metadata, event_id: (data.metadata?.event_id || crypto.randomUUID()) } });
export const trackAddToCart   = (data = {})    => track("add_to_cart",           { ...data, metadata: { ...data.metadata, event_id: (data.metadata?.event_id || crypto.randomUUID()) } });
export const trackStartCheckout     = (d = {}) => track("start_checkout",        { ...d, metadata: { ...d.metadata, event_id: (d.metadata?.event_id || crypto.randomUUID()) } });
export const trackPaymentLink       = (d = {}) => track("generate_payment_link", d);
export const trackPurchase          = (d = {}) => track("purchase",              { ...d, metadata: { ...d.metadata, event_id: (d.metadata?.event_id || crypto.randomUUID()) } });
export const trackLeadCaptured      = (d = {}) => track("lead_captured",         { ...d, metadata: { ...d.metadata, event_id: (d.metadata?.event_id || crypto.randomUUID()) } });
export const trackAbandonedCart     = (d = {}) => track("abandoned_cart",        { ...d, metadata: { ...d.metadata, event_id: (d.metadata?.event_id || crypto.randomUUID()) } });
export const trackTableQrOpen       = (d = {}) => track("table_qr_open",        d);

export { getSessionId };
